/* eslint-disable jsx-a11y/anchor-is-valid */
import {useIntl} from 'react-intl'
import {useTranslation} from 'react-i18next'
import {Routes, Route, useLocation, useMatch} from 'react-router-dom'
import {MuiPickersUtilsProvider} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import esLocale from 'date-fns/locale/es'
import enUSLocale from 'date-fns/locale/en-US'

import {PageTitle} from '../../../_metronic/layout/core'
import {GisMap, GisMapProvider} from './GisMap'
import {DrawV2Provider} from './Map/Draw/DrawV2Context'
import {GisProvider, useGisContext} from './GisContext'
import {LeftContainerFields, AnyContainerFields} from './Fields'
import {Content} from '../../../_metronic/layout/components/Content'
import {ViewFieldProvider} from './Fields/ViewField/ViewFieldContext'
import {ViewImageProvider} from './Fields/ViewField/ViewLand/Images/ViewImageContext'
import {useViewLandContext, ViewLandProvider} from './Fields/ViewField/ViewLand/ViewLandContext'
import {MapLayersProvider} from './Fields/MapLayers'
import {MapLayersV2Provider} from './Map/MapLayersV2Context'
import {
  MapLegendProvider,
  useMapLegendContext,
  CommonMapLegend,
  AmbientMapLegend,
  ComparisonMapLegends,
  shouldRenderMapLegends,
  shouldShowAmbientLegend,
} from './Fields/ViewField/ViewLand/Helpers/MapLegend'
import {Toolbar, MobileContent} from './Toolbar'
import {SearchProvider} from './Toolbar/SearchContext'
import {LeftContainerReports} from './Reports'
import {EconomicReportProvider} from './Reports/EconomicReport/EconomicReportContext'
import {CreateEconomicReportProvider} from './Reports/EconomicReport/CreateEconomicReport/CreateEconomicReportContext'
import {DrawEndButtonProvider} from './Map/Draw/DrawEndButton/DrawEndButtonContext'
import DrawEndButton from './Map/Draw/DrawEndButton/DrawEndButton'
import RightDrawer from './RightDrawer'
import {useIsMobile} from '../../shared/responsive'
import {useDrawV2} from './Map/Draw/DrawV2Context'
import GisFieldsBootstrap from './GisFieldsBootstrap'
import ReportsMapCleanup from './ReportsMapCleanup'
import './Gis.style.scss'

const pickerLocales = {
  es: esLocale,
  en: enUSLocale,
}

const Legends = () => {
  const mapLegendContext = useMapLegendContext()
  const viewLandContext = useViewLandContext()
  const isLandRoute = useMatch('/gis/field/view/:field_id/land/view/:land_id/*')
  const isComparisonSection = viewLandContext.activeSection === 'comparison'
  const hasComparisonLegends =
    isComparisonSection &&
    mapLegendContext.comparisonLegends?.land_id === viewLandContext.land?.land_id &&
    (mapLegendContext.comparisonLegends?.items || []).length > 0
  const shouldShowBasicLegend =
    !isComparisonSection &&
    mapLegendContext.ambient == null &&
    mapLegendContext.basicLegend != null &&
    viewLandContext.land &&
    mapLegendContext.activeLand == viewLandContext.land.land_id
  const showAmbientLegend = shouldShowAmbientLegend({
    activeSection: viewLandContext.activeSection,
    ambient: mapLegendContext.ambient,
  })

  if (!shouldRenderMapLegends({
    isLandRoute,
    hasComparisonLegends,
    shouldShowBasicLegend,
    showAmbientLegend,
  })) {
    return null
  }

  return (
    <>
      {hasComparisonLegends && (
        <ComparisonMapLegends items={mapLegendContext.comparisonLegends.items} />
      )}
      {shouldShowBasicLegend && <CommonMapLegend headers={mapLegendContext.basicLegend} />}
      {showAmbientLegend && <AmbientMapLegend ambient={mapLegendContext.ambient} />}
    </>
  )
}

const DesktopMapLegends = () => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return null
  }

  return (
    <div className='gis-desktop-legend'>
      <Legends />
    </div>
  )
}

const ContentX = () => {
  return (
    <>
      <DrawEndButton />
      <Routes>
        <Route path='/field/*' element={<LeftContainerFields />} />
        <Route path='/reports/*' element={<LeftContainerReports />} />
      </Routes>
    </>
  )
}

const MobileDrawerContent = () => {
  const {isOverviewView, shouldShowRouteContent} = useGisContext()

  if (isOverviewView) {
    return <MobileContent />
  }

  return (
    <>
      {shouldShowRouteContent && <ContentX />}
    </>
  )
}

const ReportRouteProviders = ({children}) => {
  const {pathname} = useLocation()
  const isEconomicReportRoute = pathname.startsWith('/gis/reports/economic')
  const isEconomicReportCreation = pathname.startsWith('/gis/reports/economic/new')

  if (!isEconomicReportRoute) {
    return children
  }

  return (
    <EconomicReportProvider>
      {isEconomicReportCreation ? (
        <CreateEconomicReportProvider>{children}</CreateEconomicReportProvider>
      ) : (
        children
      )}
    </EconomicReportProvider>
  )
}

const UsingNonFlex = () => {
  const isMobile = useIsMobile()

  return (
    <>
      {isMobile && (
        <RightDrawer>
          <MobileDrawerContent />
        </RightDrawer>
      )}
      {!isMobile && (
        <div className='right-gis-container pb-5'>
          <div className='d-flex flex-column flex-row-fluid w-400px'>
            <ContentX />
          </div>
        </div>
      )}
    </>
  )
}

const GisPageShell = ({children}) => {
  const isMobile = useIsMobile()
  const draw = useDrawV2()

  return (
    <div
      id='kt_content'
      className='p-0 content d-flex flex-column flex-column-fluid gis-page-shell'
      data-gis-mobile-draw-active={isMobile && draw.isDrawing ? 'true' : 'false'}
    >
      {children}
    </div>
  )
}

const GisModule = () => {
  const intl = useIntl()
  const {i18n} = useTranslation()
  const isMobile = useIsMobile()
  const pickerLanguage = (i18n.resolvedLanguage || i18n.language || 'es').split('-')[0]
  const pickerLocale = pickerLocales[pickerLanguage] || esLocale

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={pickerLocale}>
      <GisProvider>
        <GisMapProvider>
          <SearchProvider>
            <DrawV2Provider>
              <DrawEndButtonProvider>
                <MapLayersProvider>
                  <MapLayersV2Provider>
                    <ViewFieldProvider>
                      <ViewLandProvider>
                        <MapLegendProvider>
                          <ViewImageProvider>
                            <ReportsMapCleanup />
                            <PageTitle breadcrumbs={[]}>
                              {intl.formatMessage({id: 'MENU.DASHBOARD'})}
                            </PageTitle>
                            <GisPageShell>
                              <GisFieldsBootstrap />
                              {!isMobile && <Toolbar />}
                              <div className='post d-flex flex-column-fluid' id='kt_post'>
                                <Content>
                                  <ReportRouteProviders>
                                    <UsingNonFlex />
                                  </ReportRouteProviders>
                                  <DesktopMapLegends />
                                  <GisMap />
                                  {!isMobile && (
                                    <Routes>
                                      <Route path='/field/*' element={<AnyContainerFields />} />
                                    </Routes>
                                  )}
                                </Content>
                              </div>
                            </GisPageShell>
                          </ViewImageProvider>
                        </MapLegendProvider>
                      </ViewLandProvider>
                    </ViewFieldProvider>
                  </MapLayersV2Provider>
                </MapLayersProvider>
              </DrawEndButtonProvider>
            </DrawV2Provider>
          </SearchProvider>
        </GisMapProvider>
      </GisProvider>
    </MuiPickersUtilsProvider>
  )
}

export {GisModule}
